import type { SortKey } from '@/lib/api/params'
import type { Category, EventListItem, MarketListItem, Platform } from '@/types/market'

// Re-export for convenience
export type { SortKey }

// View mode for UI
export type ViewMode = 'tiles' | 'list'
export const VIEW_MODES = ['tiles', 'list'] as const satisfies readonly ViewMode[]

// List type - what to show in list view
export type ListType = 'events' | 'markets'
export const LIST_TYPES = ['events', 'markets'] as const satisfies readonly ListType[]

// Sort config - direction is always 'desc' (APIs don't support ascending reliably)
export type SortConfig = {
	key: SortKey
	direction: 'desc'
}

export type FilterConfig = {
	platforms?: Platform[]
	categories?: Category[]
	search?: string
	// Only show active markets (default: true)
	activeOnly?: boolean
	minVolume?: number
	maxVolume?: number
	minLiquidity?: number
	maxLiquidity?: number
	minMarkets?: number
	// Filter to only show markets tradable onchain
	// - Kalshi: passes isInitialized=true to API
	// - Polymarket: no-op (all markets are tradable)
	tradableOnly?: boolean
	// Filter to only show events expiring soon
	expiringSoon?: boolean
	// Closing time filter - dynamic duration like '24h', '7d', '30d'
	closingTime?: string
	// Minimum probability percentage (0-100)
	minProbability?: number
}

export const DEFAULT_SORT: SortConfig = {
	key: 'volume',
	direction: 'desc',
}

export const DEFAULT_FILTERS: FilterConfig = { activeOnly: true }

// Check if platform is enabled in filters
export function isPlatformEnabled(platform: Platform, filters: FilterConfig): boolean {
	if (!filters.platforms || filters.platforms.length === 0) {
		return true
	}
	return filters.platforms.includes(platform)
}

// Compare for merge sort (events) - always descending
export function compareEvents(a: EventListItem, b: EventListItem, sort: SortConfig): number {
	const getValue = (e: EventListItem): number => {
		switch (sort.key) {
			case 'volume':
				return e.volume
			case 'volume24h':
				return e.volume24h
			case 'liquidity':
				return e.liquidity
			case 'openInterest':
				return e.volume
			case 'startDate':
				return e.volume
			default:
				return e.volume
		}
	}

	const diff = getValue(b) - getValue(a)

	if (diff === 0) {
		if (a.platform !== b.platform) {
			return a.platform === 'kalshi' ? -1 : 1
		}
		return a.id.localeCompare(b.id)
	}

	return diff
}

// Compare for merge sort (markets) - always descending
export function compareMarkets(a: MarketListItem, b: MarketListItem, sort: SortConfig): number {
	const getValue = (m: MarketListItem): number => {
		switch (sort.key) {
			case 'volume':
				return m.volume
			case 'volume24h':
				return m.volume24h
			case 'liquidity':
				return m.liquidity
			case 'openInterest':
				return m.openInterest
			case 'startDate':
				return m.closeTime
			default:
				return m.volume
		}
	}

	const diff = getValue(b) - getValue(a)

	if (diff === 0) {
		if (a.platform !== b.platform) {
			return a.platform === 'kalshi' ? -1 : 1
		}
		return a.id.localeCompare(b.id)
	}

	return diff
}

// Check if any filters are active (beyond defaults)
export function hasActiveFilters(filters: FilterConfig): boolean {
	if (filters.search && filters.search.trim().length > 0) return true
	if (filters.categories && filters.categories.length > 0) return true
	if (filters.minVolume !== undefined) return true
	if (filters.maxVolume !== undefined) return true
	if (filters.minLiquidity !== undefined) return true
	if (filters.maxLiquidity !== undefined) return true
	if (filters.expiringSoon) return true
	if (filters.closingTime) return true
	if (filters.minProbability !== undefined) return true
	return false
}

// Check if only backend-supported platforms are selected
export function hasOnlyBackendPlatforms(platforms: Platform[]): boolean {
	const backendPlatforms: Platform[] = ['polymarket', 'kalshi']
	return platforms.every(p => backendPlatforms.includes(p))
}

// Check if any legacy platforms are in the selection
export function hasLegacyPlatforms(platforms: Platform[]): boolean {
	const legacyPlatforms: Platform[] = ['opinionlabs', 'predictfun']
	return platforms.some(p => legacyPlatforms.includes(p))
}
